import React, { useState, useRef, useEffect } from 'react';
import { performanceMonitor } from '../../utils/performanceMonitor';

interface OptimizedImageProps {
  src: string;
  alt: string;
  className?: string;
  placeholder?: string;
  priority?: boolean;
  onLoad?: () => void;
  onError?: () => void;
  style?: React.CSSProperties;
}

const OptimizedImage: React.FC<OptimizedImageProps> = ({
  src,
  alt,
  className = '',
  placeholder = '🖼️',
  priority = false,
  onLoad,
  onError,
  style,
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(priority);
  const [hasError, setHasError] = useState(false);
  const [showPlaceholder, setShowPlaceholder] = useState(true);
  const imgRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (priority || isInView) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observerRef.current?.disconnect();
          }
        });
      },
      {
        rootMargin: '100px', // Start loading 100px before image comes into view
        threshold: 0.1,
      }
    );

    if (containerRef.current) {
      observerRef.current.observe(containerRef.current);
    }

    return () => {
      observerRef.current?.disconnect();
    };
  }, [priority, isInView]);

  const handleLoad = () => {
    setIsLoaded(true);
    setShowPlaceholder(false);

    // Performance monitoring
    const imageId = src.split('/').pop() || 'unknown';
    performanceMonitor.endTiming(`image_${imageId}`);

    onLoad?.();
  };

  const handleError = () => {
    setHasError(true);
    setShowPlaceholder(false);
    onError?.();
  };

  // Error state with cool design
  if (hasError) {
    return (
      <div
        ref={containerRef}
        className={`relative overflow-hidden ${className}`}
        style={style}
      >
        {/* Gradient background */}
        <div className="w-full h-full bg-gradient-to-br from-red-900/20 to-dark-800 flex items-center justify-center">
          <svg className="w-12 h-12 text-red-400/60" fill="currentColor" viewBox="0 0 24 24">
            <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17l2.5-3.16L14.5 17H9z" />
          </svg>
        </div>

        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent"></div>

        {/* Error indicator */}
        <div className="absolute bottom-2 left-2 right-2">
          <div className="text-xs text-red-400/80 text-center">Failed to load</div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`relative overflow-hidden ${className}`}
      style={style}
    >
      {/* Cool placeholder while loading */}
      {showPlaceholder && (
        <div className="absolute inset-0 animate-pulse">
          {/* Gradient background */}
          <div className="w-full h-full bg-gradient-to-br from-dark-700 to-dark-800 flex items-center justify-center">
            <svg className="w-12 h-12 text-dark-600" fill="currentColor" viewBox="0 0 24 24">
              <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17l2.5-3.16L14.5 17H9z" />
            </svg>
          </div>

          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent"></div>

          {/* Loading indicator */}
          {!isInView && (
            <div className="absolute bottom-2 left-2 right-2">
              <div className="h-2 bg-dark-600 rounded-full mb-1"></div>
              <div className="h-2 bg-dark-600 rounded-full w-3/4"></div>
            </div>
          )}
        </div>
      )}

      {/* Main image - only render when in view */}
      {isInView && (
        <img
          ref={imgRef}
          src={src}
          alt={alt}
          className={`w-full h-full object-cover transition-opacity duration-500 ${isLoaded ? 'opacity-100' : 'opacity-0'
            }`}
          onLoad={handleLoad}
          onError={handleError}
          loading={priority ? 'eager' : 'lazy'}
          decoding="async"
          onLoadStart={() => {
            // Start performance monitoring
            const imageId = src.split('/').pop() || 'unknown';
            performanceMonitor.startTiming(`image_${imageId}`);
          }}
        />
      )}
    </div>
  );
};

export default OptimizedImage;